<template>
    <a-card :title="$t('screenTitle.giftCodeManage')">
        <div v-if="hasMasterAdminPermission" class="mb-20">
            <a-button type="primary" @click="() => (isShowCreateNewTypesModal = true)">
                <template #icon>
                    <PlusCircleOutlined />
                </template>{{ $t('text.createNewGiftCodeType') }}
            </a-button>
        </div>
        <a-table :dataSource="typesDataSource" :columns="typesColumns" :pagination="false" :loading="isLoadingTypes"
            v-model:expandedRowKeys="expandedRowKeys" :row-key="(record) => record.id" :scroll="{x: '100%'}">
            <template #bodyCell="{ column, record, text }">
                <template v-if="column.dataIndex === 'keygen'">
                    <pre>{{ text }}</pre>
                </template>
                <template v-else-if="column.key === 'action'">
                    <a-space>
                        <a-button v-if="hasMasterAdminPermission" type="primary" danger
                            @click="deleteTypeConfirm(record)">
                            <template #icon>
                                <DeleteOutlined />
                            </template>
                        </a-button>
                    </a-space>
                </template>
            </template>

            <template #expandedRowRender="{ record }">
                <div v-if="record.keygen.indexOf('.') !== -1">
                    <a-form :name="`formGift-${record.id}`" @submit="() => onFinishGift(record)"
                        :scrollToFirstError="true" layout="inline">
                        <a-form-item :label="$t('labelText.giftcode_type_name')" :required="true" hidden>
                            <a-input :value="record.id" readonly />
                        </a-form-item>

                        <a-form-item :label="$t('text.num')" :required="true">
                            <a-input-number v-model:value="record.num" :min="1" :max="20" />
                        </a-form-item>

                        <a-form-item>
                            <a-button type="primary" html-type="submit" :loading="record.isLoadingGift"
                                :disabled="!record.num">{{ $t('text.create') }}</a-button>
                        </a-form-item>
                    </a-form>

                    <a-alert v-if="record.createdGifts?.length" type="success" class="mt-20">
                        <template #description>
                            <a-typography-paragraph copyable>
                                <template #copyableIcon="{ copied }">
                                    <div style="font-size: 24px;">
                                        <CopyOutlined v-if="!copied" />
                                        <CheckOutlined v-else />
                                    </div>
                                </template>
                                <pre>{{
                                'Xin gửi tặng bạn ' + record.createdGifts?.length + ' mã GiftCode ['+record.name+']:' + '\n' + record.createdGifts?.join('\n') + '\n' + 'Chúc bạn chơi game vui vẻ!'
                            }}</pre>
                            </a-typography-paragraph>
                        </template>
                    </a-alert>
                </div>
                <div v-else>
                    <a-alert type="success">
                        <template #description>
                            <a-typography-paragraph copyable>
                                <template #copyableIcon="{ copied }">
                                    <div style="font-size: 24px;">
                                        <CopyOutlined v-if="!copied" />
                                        <CheckOutlined v-else />
                                    </div>
                                </template>
                                <pre>{{
                                'Xin gửi tặng bạn GiftCode ['+record.name+']:' + '\n' + record.keygen + '\n' + 'Chúc bạn chơi game vui vẻ!'
                            }}</pre>
                            </a-typography-paragraph>
                        </template>
                    </a-alert>
                </div>
            </template>
        </a-table>

        <a-modal v-if="hasMasterAdminPermission" :open="isShowCreateNewTypesModal"
            @cancel="() => (isShowCreateNewTypesModal = false)" :title="$t('text.createNewGiftCodeType')"
            :footer="false">
            <a-form ref="formType" :model="formModelType" @finish="onFinishType" :rules="formRuleType"
                :scrollToFirstError="true" layout="vertical">
                <a-form-item :label="$t('labelText.giftcode_type_name')" name="name">
                    <a-input v-model:value="formModelType.name" />
                </a-form-item>

                <a-form-item
                    :label="$t('labelText.giftcode_type_keygen') + ' (' +$t('message.giftCodeTypeKeygenHelp')+ ')'"
                    name="keygen">
                    <a-input v-model:value="formModelType.keygen" placeholder="Ke...yGen....XyZ.." />
                </a-form-item>

                <a-form-item>
                    <a-button type="primary" html-type="submit" block :loading="isSavingType">{{ $t('text.save') }}
                    </a-button>
                </a-form-item>
            </a-form>
        </a-modal>
    </a-card>
</template>

<script lang="ts">
import { computed, defineComponent, onMounted, reactive, ref, } from "vue";

import { Alert, Button, Card, Form, Input, InputNumber, message, Modal, Space, Table, TableColumnType, Typography, } from "ant-design-vue";
import { DeleteOutlined, PlusCircleOutlined, CheckOutlined, CopyOutlined, } from '@ant-design/icons-vue';

import { RouteName } from "@/router";
import ApiRequest from "@/utils/ApiRequest";
import { useI18n } from "vue-i18n";
import { RuleObject } from "ant-design-vue/lib/form";
import { useStore } from "vuex";
import { processEnv } from "@/utils/helpers";

interface FormModelType {
    name: string;
    keygen: string;
}

export default defineComponent({
    components: {
        ACard: Card,
        ATable: Table,
        AButton: Button,
        ASpace: Space,
        AModal: Modal,
        AForm: Form,
        AFormItem: Form.Item,
        AInput: Input,
        AInputNumber: InputNumber,
        ATypographyParagraph: Typography.Paragraph,
        AAlert: Alert,

        DeleteOutlined,
        PlusCircleOutlined,
        CopyOutlined,
        CheckOutlined,
    },

    setup() {
        const { t } = useI18n();
        const store = useStore();

        const expandedRowKeys = ref<string[]>([]);
        const isLoadingTypes = ref(true);
        const typesDataSource = ref([]);
        const typesColumns: TableColumnType[] = [
            {
                title: t('labelText.giftcode_type_name'),
                dataIndex: 'name',
            },
            {
                title: t('labelText.giftcode_type_keygen'),
                dataIndex: 'keygen',
            },
            {
                title: t('labelText.created_at'),
                dataIndex: "created_at",
                responsive: ['md'],
            },
            {
                title: t('text.action'),
                key: 'action',
            },
        ];
        const fetchTypesData = async () => {
            isLoadingTypes.value = true;

            return ApiRequest.get('/giftcodes/types').then(res => {
                const resData = res.data;

                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                typesDataSource.value = resData.data.map((v: any) => {
                    const splitKVvalue = v.value.split('|');
                    v.keygen = splitKVvalue[0];
                    v.name = splitKVvalue[1];
                    return v;
                });
            })
            .finally(() => {
                isLoadingTypes.value = false;
            });
        };

        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const deleteTypeConfirm = (record: any) => {
            Modal.confirm({
                title: t('message.deleteConfirmContent'),
                content: record.value,
                autoFocusButton: 'cancel',
                maskClosable: true,
                onOk: async () => {
                    return ApiRequest.del('/giftcodes/types/' + record.id).then(() => {
                        message.success(t('message.deleteSuccess'));
                    })
                    .finally(() => {
                        fetchTypesData();
                    });
                },
            });
        };

        const formType = ref<typeof Form>();
        const isSavingType = ref(false);
        const isShowCreateNewTypesModal = ref(false);
        const formModelType = reactive<FormModelType>({
            name: '',
            keygen: '',
        });
        const formExtraErrorType: Record<keyof FormModelType, string> = {
            name: '',
            keygen: '',
        };
        const formRuleType = reactive<Record<keyof FormModelType, RuleObject[]>>({
            name: [
                {
                    required: true,
                    trigger: 'change',
                },
            ],
            keygen: [
                {
                    required: true,
                    len: 18,
                    trigger: 'change',
                },
                {
                    pattern: /^[a-zA-Z0-9.]+$/,
                    trigger: 'change',
                },
                {
                    validator: (_rule, _value, callback) => callback(formExtraErrorType.keygen || undefined),
                },
            ],
        });
        const onFinishType = (values: FormModelType) => {
            isSavingType.value = true;
            ApiRequest.post('/giftcodes/types', values).then(() => {
                message.success(t('message.createSuccess'));

                fetchTypesData();
            })
                .catch(err => {
                    ApiRequest.handleError(err, formExtraErrorType, formType.value);
                })
                .finally(() => {
                    isSavingType.value = false;
                });
        };

        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const onFinishGift = (record: any) => {
            record.isLoadingGift = true;

            ApiRequest.post('/giftcodes/gen', {
                id: record.id,
                num: record.num,
            })
                .then(res => {
                    record.createdGifts = res.data.data;

                    message.success(t('message.createSuccess'));
                })
                .finally(() => {
                    setTimeout(() => {
                        record.isLoadingGift = false;
                    }, 2000);
                });
        };

        onMounted(() => {
            fetchTypesData();
        });

        return {
            typesDataSource,
            typesColumns,

            isLoadingTypes,
            fetchTypesData,
            deleteTypeConfirm,
            expandedRowKeys,

            isSavingType,
            formModelType,
            formRuleType,
            formType,
            onFinishType,
            isShowCreateNewTypesModal,

            onFinishGift,

            processEnv,

            RouteName,

            hasMasterAdminPermission: computed(() => store.getters.hasMasterAdminPermission),
        };
    },
})
</script>

<style scoped>

</style>
